<template>
  <div class="consultant-certificates">
    <!-- Sidebar de solicitação de certificado -->
    <consultant-certificate-sidebar />

    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-1">
      <!-- Título -->
      <h1 class="mb-0">Certificados</h1>

      <!-- Ações -->
      <div>
        <!-- Botão futuro de gestão -->
        <b-button
          v-if="$ability.can(MANAGE_ACTION, CERTIFICATE_MANAGEMENT_MENU)"
          variant="outline-primary"
          size="sm"
          class="mr-50"
          @click="goToCertificatesManagement"
        >
          <feather-icon icon="ListIcon" class="mr-50" />
          Gestão de certificados
        </b-button>

        <!-- Botão para abrir sidebar -->
        <b-button
          v-if="canRequest"
          variant="primary"
          size="sm"
          @click="openRequestSidebar"
        >
          <feather-icon icon="AwardIcon" class="mr-50" />
          Solicitar certificado
        </b-button>
      </div>
    </div>

    <!-- Solicitações ainda não aprovadas (o certificado só entra na lista abaixo depois da aprovação final) -->
    <b-card v-if="requests.length" title="Solicitações">
      <b-table
        :items="requests"
        :fields="requestFields"
        small
        responsive="sm"
      >
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
          <div
            v-if="data.item.rejection_reason"
            class="text-muted small mt-25"
          >
            Motivo: {{ data.item.rejection_reason }}
          </div>
        </template>

        <template #cell(created_at)="data">
          <span>{{ data.item.created_at | dateTime }}</span>
        </template>

        <template #cell(actions)="data">
          <b-button
            :variant="hasReviewAction(data.item) ? 'primary' : 'link'"
            size="sm"
            @click="openRequest(data.item.id)"
          >
            <feather-icon
              :icon="hasReviewAction(data.item) ? 'CheckSquareIcon' : 'EyeIcon'"
              :class="{ 'mr-50': hasReviewAction(data.item) }"
            />
            <span v-if="hasReviewAction(data.item)">Avaliar</span>
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Lista de certificados -->
    <b-card>
      <b-table
        :items="certificates"
        :fields="tableFields"
        :busy="loading"
        small
        responsive="sm"
        show-empty
        empty-text="Nenhum certificado encontrado para este PN."
      >
        <!-- Status formatado -->
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
        </template>

        <!-- Datas -->
        <template #cell(requested_at)="data">
          <span>{{ data.item.requested_at | dateTime }}</span>
        </template>

        <template #cell(approved_at)="data">
          <!-- approved_at vai no JSON como UTC ("...Z") e o filtro dateTime ignora o fuso -->
          <span v-if="data.item.approved_at_formatted">
            {{ data.item.approved_at_formatted }}
          </span>
          <span v-else>-</span>
        </template>
        <template #cell(view)="data">
          <b-button
            v-if="data.item.certificate_file_url"
            variant="link"
            size="sm"
            @click="openPdf(data.item.id)"
          >
            <feather-icon icon="EyeIcon" />
          </b-button>

          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(history)="data">
          <b-button
            variant="link"
            size="sm"
            @click="openRequest(data.item.id)"
          >
            <feather-icon icon="ClockIcon" />
          </b-button>
        </template>

        <!-- Loading interno da tabela -->
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner small class="mr-1" />
            Carregando certificados...
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BButton,
  BBadge,
  BSpinner,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
} from "bootstrap-vue";

import ConsultantCertificateSidebar from "../components/ConsultantCertificateSidebar.vue";
import * as types from "../store/types";
import { mapMutations, mapActions } from "vuex";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { MANAGE_ACTION, CERTIFICATE_MANAGEMENT_MENU } from "@/constants/resources";
import { certificateStatusLabel, certificateStatusVariants } from "@/constants/certificates_status";

export default {
  name: "ConsultantCertificates",

  components: {
    BCard,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    ConsultantCertificateSidebar,
  },

  data() {
    return {
      MANAGE_ACTION,
      CERTIFICATE_MANAGEMENT_MENU,
      consultantId: Number(this.$route.params.id),
      loading: false,
      certificates: [],
      loadingCertificateTypes: false,
      certificateTypes: [],
      selectedCertificateTypeId: null,
      submittingRequest: false,
      canRequest: false,
      consultantName: undefined,
      requests: [],
      tableFields: [
        { key: "certificate_type_name", label: "Curso" },
        { key: "status", label: "Status" },
        { key: "requested_by_name", label: "Solicitado por" },
        { key: "approved_at", label: "Aprovado em" },
        { key: "view", label: "Visualizar", class: "text-center" },
        { key: "history", label: "Histórico", class: "text-center" },
      ],
      requestFields: [
        { key: "certificate_type_name", label: "Curso" },
        { key: "status", label: "Status" },
        { key: "origin_label", label: "Origem" },
        { key: "requested_by_name", label: "Solicitado por" },
        { key: "created_at", label: "Solicitado em" },
        { key: "actions", label: "Ações", class: "text-center" },
      ],
    };
  },

  computed: {
    certificateTypeOptions() {
      return this.certificateTypes.map((ct) => ({
        value: ct.id,
        text: ct.name,
      }));
    },
  },

  mounted() {
    this.loadRequests().then(() => this.openCertificateSidebarFromRoute());
    this.loadCertificatesFromSummary();
  },

  methods: {
  ...mapMutations({
    mutateConsultantCertificateSidebar: types.MUTATE_CONSULTANT_CERTIFICATE_SIDEBAR,
  }),

  ...mapActions({
  downloadCertificate: types.DOWNLOAD_CERTIFICATE,
  getCertificateRequests: types.GET_CONSULTANT_CERTIFICATE_REQUESTS,
  }),

  loadRequests() {
    return this.getCertificateRequests(this.consultantId)
      .then((resp) => {
        this.canRequest = !!resp.data.can_request;
        this.consultantName = resp.data.consultant?.name;
        this.requests = resp.data.requests || [];
      })
      .catch(() => {
        this.canRequest = false;
        this.requests = [];
      });
  },

  reload() {
    this.loadRequests();
    this.loadCertificatesFromSummary();
  },

  hasReviewAction(item) {
    return !!(item.available_actions?.leader_review || item.available_actions?.final_review);
  },

  openRequest(id) {
    this.mutateConsultantCertificateSidebar({
      visible: true,
      consultant_id: this.consultantId,
      consultant_name: this.consultantName,
      id,
      saveAction: () => this.reload(),
    });
  },

  async openPdf(id) {
  try {
    const response = await this.downloadCertificate(id)

    const fileURL = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))

    const link = document.createElement('a')
    link.href = fileURL
    link.setAttribute('download', `certificado_${id}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()

    window.URL.revokeObjectURL(fileURL)
  } catch (e) {
    console.error('Erro ao baixar certificado:', e)

    this.$toast({
      component: ToastificationContent,
      props: {
        title: 'Erro',
        icon: 'XIcon',
        variant: 'danger',
        text: 'Erro ao baixar o certificado.',
      },
    })
  }
},

  statusVariant(status) {
    return certificateStatusVariants[status] || "secondary";
  },

  statusLabel(status) {
    return certificateStatusLabel(status);
  },

  async loadCertificatesFromSummary() {
    this.loading = true;

    try {
      const resp = await this.$store.dispatch(types.GET_CONSULTANT, this.consultantId);

      this.certificates = (resp.data.certificates || []).map(c => ({
        ...c,
        certificate_type_name: c.certificate_type?.name || "-",
        requested_by_name: c.requested_by?.name || "-",
        approved_by_name: c.approved_by?.name || "-"
      }));
    } catch (e) {
      console.error("Erro ao carregar certificados:", e);
      this.$toast.error("Erro ao carregar certificados.");
    } finally {
      this.loading = false;
    }
  },
    
    openCertificateSidebarFromRoute() {
      const { certificateModal, openModal } = this.$route.query;
      if ((!certificateModal && !openModal) || !this.canRequest) return;

      this.openRequestSidebar();
    },

    openRequestSidebar() {
      this.mutateConsultantCertificateSidebar({
        visible: true,
        consultant_id: this.consultantId,
        consultant_name: this.consultantName,
        id: undefined,
        saveAction: () => this.reload(),
      });
    },

    goToCertificatesManagement() {
      this.$router.push({ name: "certificate-management" });
    },

    closeRequestSidebar() {
      this.selectedCertificateTypeId = null;
    },

    async submitRequest() {
      if (!this.selectedCertificateTypeId) {
        return;
      }

      this.submittingRequest = true;
      try {
        await this.$http.post("/consultant_certificates", {
          consultant_id: this.consultantId,
          certificate_type_id: this.selectedCertificateTypeId,
        });

        this.$toast.success("Certificado solicitado com sucesso");
        this.fetchCertificates && this.fetchCertificates();
      } catch (e) {
        this.$toast.error("Erro ao solicitar certificado");
        console.error("Erro ao solicitar certificado", e);
      } finally {
        this.submittingRequest = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
